import type { PageSectionLayout } from '../services/sectionLayoutService';
import type { Track } from '../types/domain';
import { loadTrackAliasOrderMap } from './trackAliasOrderStorage';
import { buildTrackNameByTimetableTextByGrade } from './programProgressRows';
import { matchesTrackWildcardSuffix, sanitizeWildcardChar } from './trackWildcard';

export interface GradeTrackTimetableContext {
  gradeId: string;
  trackNameByTimetableText: Record<string, string>;
  wildcardCharByTrackName: Record<string, string>;
  defaultAliasByTrackName: Record<string, string>;
}

/** 편제블럭·저장 순서 기준 시간표명 배열(첫 항목이 와일드카드 기본 시간표) */
export function orderedTrackAliasesForGrade(
  workspaceId: string,
  gradeId: string,
  trackName: string,
  tracks: Track[]
): string[] {
  const normalizedTrack = trackName.trim();
  if (!gradeId || !normalizedTrack) return [];
  const aliases = Array.from(
    new Set(
      tracks
        .filter((t) => (t.grade_id ?? '') === gradeId && (t.name ?? '').trim() === normalizedTrack)
        .map((t) => (t.description ?? '').trim())
        .filter(Boolean)
    )
  );
  const stored = loadTrackAliasOrderMap(workspaceId, gradeId)[normalizedTrack] ?? [];
  if (!stored.length) return aliases;
  return [
    ...stored.filter((alias) => aliases.includes(alias)),
    ...aliases.filter((alias) => !stored.includes(alias)),
  ];
}

export function buildGradeTrackTimetableContext(
  workspaceId: string,
  gradeId: string,
  tracks: Track[],
  gradeTrackSections?: PageSectionLayout[] | null
): GradeTrackTimetableContext | null {
  const gid = gradeId.trim();
  if (!gid) return null;
  const trackNameByTimetableText = buildTrackNameByTimetableTextByGrade(tracks)[gid] ?? {};
  const wildcardCharByTrackName: Record<string, string> = {};
  (gradeTrackSections ?? []).forEach((section) => {
    if (!section.gradeIds.includes(gid)) return;
    (section.trackColumns ?? []).forEach((column) => {
      const trackName = (column.name ?? '').trim();
      const wildcard = sanitizeWildcardChar(column.wildcardChar ?? '');
      if (trackName && wildcard) wildcardCharByTrackName[trackName] = wildcard;
    });
  });
  const defaultAliasByTrackName: Record<string, string> = {};
  const trackNames = Array.from(
    new Set(
      tracks
        .filter((t) => (t.grade_id ?? '') === gid)
        .map((t) => (t.name ?? '').trim())
        .filter(Boolean)
    )
  );
  trackNames.forEach((trackName) => {
    defaultAliasByTrackName[trackName] = orderedTrackAliasesForGrade(workspaceId, gid, trackName, tracks)[0] ?? '';
  });
  return {
    gradeId: gid,
    trackNameByTimetableText,
    wildcardCharByTrackName,
    defaultAliasByTrackName,
  };
}

export function resolveTrackNameFromTimetableText(
  timetableText: string,
  context: GradeTrackTimetableContext
): string {
  const text = timetableText.trim();
  if (!text) return '';
  const exact = context.trackNameByTimetableText[text];
  if (exact) return exact;
  for (const [trackName, wildcardChar] of Object.entries(context.wildcardCharByTrackName)) {
    if (matchesTrackWildcardSuffix(text, wildcardChar)) return trackName;
  }
  return '';
}

/**
 * 와일드카드 `(X)` 접미사 시간표는 편제의 첫 번째 시간표명으로 정규화한다.
 * 시수 집계·프로그램 페이지 행 구성에 공통 사용.
 */
export function normalizeTimetableTextToDefaultAlias(
  timetableText: string,
  context: GradeTrackTimetableContext,
  trackNameHint?: string
): string {
  const raw = timetableText.trim();
  if (!raw) return raw;
  const trackName = (trackNameHint ?? '').trim() || resolveTrackNameFromTimetableText(raw, context);
  if (!trackName) return raw;
  const wildcard = context.wildcardCharByTrackName[trackName] ?? '';
  if (!wildcard || !matchesTrackWildcardSuffix(raw, wildcard)) return raw;
  return context.defaultAliasByTrackName[trackName] || raw;
}
